import { Modal } from "@JohnLewisPartnership/jl-design-system/dist/components/modal/Modal";
import { Variant } from "@/types/types";
import EfficiencyPanel from "./EfficiencyPanel/EfficiencyPanel";
import {
  EnergyLabelsResponse,
  EnergyLabelsRequestParams,
} from "../../data/EnergyLabelsFetcher";
import styles from "./EnergySavingModal.module.scss";
import UsageControls from "./UsageControls/UsageControls";
import {
  getAverageUsage,
  getTariff,
  updateRequestParams,
  mapRequestParams,
} from "../../utils";
import { useEffect, useState } from "react";
import { EnergyLabelInputValues } from "../../EnergySavingUI";
import { EnergyLabelMetaAverageUsage } from "../../data/energyLabelResponseSchema";
import CheaperAlternatives from "@/components/EnergySaving/components/EnergySavingModal/CheaperAlternatives/CheaperAlternatives.tsx";
import InfoPanel from "@/components/EnergySaving/components/EnergySavingModal/InfoPanel/InfoPanel.tsx";

export type Props = {
  variant: Variant;
  sellingProductType: string;
  updateEnergyLabels: (requestParams: EnergyLabelsRequestParams | null) => void;
  updateResponse: EnergyLabelsResponse;
  isOpen: boolean;
  onClose: () => void;
  onUpdateUsageParams: (paramName: string, paramValue: string | number) => void;
  previousUsageParams: EnergyLabelInputValues | null;
};

export type UsageControlParams = {
  [index: string]: number | null;
  customUsageTimesPerPeriod: number | null;
  customUsagePeriod: number | null;
  customEnergyCostYears: number;
  customTariff: number;
};

const poundsToPence = (amount: number) => amount * 100;

const defaultRequestParams: EnergyLabelsRequestParams = {
  customCyclesPerYear: null,
  customEnergyCostYears: null,
  customTariff: null,
};

const getDefaultControlValues = (
  updateResponse: EnergyLabelsResponse,
  productTypeAverages: EnergyLabelMetaAverageUsage,
  previousUsageParams: EnergyLabelInputValues | null,
) => {
  const {
    averageUsageTimesPerPeriod = 0,
    averageUsagePeriod = 0,
    energyCostYears,
  } = productTypeAverages;
  const tariff = getTariff(updateResponse);

  let initialControlValues: UsageControlParams = {
    customUsageTimesPerPeriod: averageUsageTimesPerPeriod,
    customUsagePeriod: averageUsagePeriod,
    customEnergyCostYears: energyCostYears,
    customTariff: tariff ? poundsToPence(tariff) : 0,
  };

  if (previousUsageParams) {
    const {
      customUsageTimesPerPeriod,
      customUsagePeriod,
      customEnergyCostYears,
      customTariff,
    } = previousUsageParams;
    initialControlValues = {
      customUsageTimesPerPeriod:
        customUsageTimesPerPeriod ||
        initialControlValues.customUsageTimesPerPeriod,
      customUsagePeriod:
        customUsagePeriod || initialControlValues.customUsagePeriod,
      customEnergyCostYears:
        customEnergyCostYears || initialControlValues.customEnergyCostYears,
      customTariff: customTariff || initialControlValues.customTariff,
    };
  }

  return initialControlValues;
};

const EnergySavingModal = ({
  variant,
  sellingProductType,
  isOpen,
  onClose,
  updateEnergyLabels,
  updateResponse,
  onUpdateUsageParams,
  previousUsageParams,
}: Props) => {
  const [requestParams, setRequestParams] = useState(defaultRequestParams);
  const [controlValues, setControlValues] = useState<UsageControlParams | null>(
    null,
  );
  const [hasChanged, setHasChanged] = useState<boolean>(false);

  // set default control values from product type averages
  useEffect(() => {
    if (!controlValues) {
      const productTypeAverages = getAverageUsage(
        sellingProductType,
        updateResponse,
      );
      if (productTypeAverages) {
        const initialControlValues = getDefaultControlValues(
          updateResponse,
          productTypeAverages,
          previousUsageParams,
        );
        setControlValues(initialControlValues);

        const initialRequestParams = mapRequestParams(initialControlValues);
        if (initialRequestParams) {
          setRequestParams(initialRequestParams);
        }
      }
    }
  }, [controlValues, sellingProductType, updateResponse, previousUsageParams]);

  // update request when control values change
  useEffect(() => {
    if (hasChanged) {
      updateEnergyLabels(requestParams);
      setHasChanged(false);
    }
  }, [requestParams, hasChanged]);

  const onChangeUsage = (paramName: string, paramValue: string | number) => {
    if (controlValues) {
      setRequestParams((prevState) => {
        const currentRequestParams = { ...prevState };
        return updateRequestParams(
          currentRequestParams,
          controlValues,
          paramName,
          Number(paramValue),
        );
      });
    }

    setControlValues((prevState) => {
      if (prevState) {
        const updatedValues = { ...prevState };
        updatedValues[paramName] = Number(paramValue);
        return updatedValues;
      }
      return prevState;
    });

    setHasChanged(true);
    onUpdateUsageParams(paramName, paramValue);
  };

  if (!controlValues) return null;

  return (
    <Modal
      rootId="page-root"
      title={"Our energy efficiency calculator"}
      isOpen={isOpen}
      onClose={onClose}
      className={styles.energyModal}
      width={"large"}
      height={"fixed"}
      primaryButtonText={"Close"}
      onPrimaryButtonClick={onClose}
      underlinedButtonText={null}
    >
      <h2 className={styles.energyModal__heading}>My usage</h2>

      <div className={styles.energyModal__myUsage}>
        <UsageControls {...controlValues} onChangeUsage={onChangeUsage} />
        <EfficiencyPanel variant={variant} updateResponse={updateResponse} />
      </div>

      <div>
        <CheaperAlternatives
          updateResponse={updateResponse}
          variant={variant}
        />
      </div>

      <div>
        <InfoPanel
          sellingProductType={sellingProductType}
          updateResponse={updateResponse}
          variant={variant}
        />
      </div>
    </Modal>
  );
};

export default EnergySavingModal;
